/**
 * API types for cart operations
 * These mirror the backend DTOs but adapted for frontend use
 */

export interface AddToCartDto {
  cartId?: string;
  productId: string;
  quantity: number;
  selectedValue: number;
  currency: string;
  country?: string;
  email?: string;
  // Meta Pixel browser eventId - when present, server fires CAPI AddToCart
  // with the same id so browser fbq + server CAPI dedupe in Events Manager.
  metaEventId?: string;
  fbp?: string;
  fbc?: string;
}

export interface UpdateCartItemDto {
  cartId: string;
  productId: string;
  quantity?: number;
  selectedValue?: number;
  productIndex: number;
  email?: string;
}

export interface RemoveFromCartDto {
  cartId: string;
  productIndex: number;
  email?: string;
}

export interface UpdateCartDetailsDto {
  cartId: string;
  email?: string;
}

export interface MergeCartRequest {
  guestCartId?: string;
}

/**
 * Cart item structure from backend (API)
 */
export interface CartItem {
  productId: string;
  quantity: number;
  selectedValue: number;
  currency: string;
  country?: string;
}

/**
 * Cart document structure from backend - matches CartModel schema
 */
export interface Cart {
  _id?: string;
  email?: string;
  orderId?: string;
  orderIds?: string[];
  isUserNotified?: boolean;
  products: CartItem[];
  deletedAt?: Date;
  // Set when this cart has been folded into another by the login-merge flow.
  // The cart still works (browsers that hold this id can keep buying), but the
  // server now considers the pointed-to cart the authoritative one for the user.
  mergedIntoCartId?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * Legacy aliases for backward compatibility
 * Note: Use CartItem and Cart directly instead of these aliases
 */
export type ApiCartItem = CartItem;
export type CartDocument = Cart;

/**
 * Response types for cart operations
 */
export type AddToCartResponse = Cart;
export type UpdateCartItemResponse = Cart;
export type RemoveFromCartResponse = Cart;
export type GetCartResponse = Cart;
export type EmptyCartResponse = Cart;
export type UpdateCartDetailsResponse = Cart;

// POST /cart/email-check - mirrors EmailCheckStatus / EmailCheckResult of
// aceb-api (contact/email-check).
export enum EmailCheckStatus {
  Ok = 'ok',
  // The domain exists, but looks like a typo of a known provider.
  Suggestion = 'suggestion',
  // No mail can reach the domain: the API refuses an order on it.
  DomainNotFound = 'domain_not_found',
}

export interface EmailCheckResponse {
  status: EmailCheckStatus;
  // The whole address with the likely domain, when there is one.
  suggestion?: string;
}
// null when the user has no guest or pending carts → caller should clear local state
export type MergeCartResponse = Cart | null;
